import React from 'react';

import {
  Add as AddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  BorderColor as BorderColorIcon,
  Delete as DeleteFilledIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { Button, Typography, useTheme } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import {
  PlatformStaffItemFragment,
  UserStatus,
  useResendInviteToPlatformStaffMutation,
} from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import type { TableColumn, TableFilter } from 'src/components/table';
import { Table, TableRowAction, TableTopAction } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useClearVerifyStaffTableList } from 'src/hooks/clearVerifyStaff';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useCurrentPlatformStaff } from 'src/hooks/platformStaff';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { MfaManagementModal } from 'src/modals/auth';
import {
  DeleteClearVerifyStaffConfirmationModal,
  PlatformStaffDetailModal,
} from 'src/modals/clearVerifyStaff';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { hasOneOfRoles } from 'src/utils/role';
import { getUserFullName } from 'src/utils/user';

export const ClearVerifyStaffTableInner: React.FC = () => {
  const theme = useTheme();

  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();
  const { createApolloContext } = useNotification();
  const { pagination, setSortOption } = useFilterStorage();

  const {
    platformStaffId,
    isAbleToManagePlatformStaff,
    loading: platformStaffLoading,
  } = useCurrentPlatformStaff();

  const { clearVerifyStaffCount, clearVerifyStaff, refetch, loading } =
    useClearVerifyStaffTableList({
      notifyOnNetworkStatusChange: true,
    });

  const [resendInviteToPlatformStaff] = useResendInviteToPlatformStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  const refetchData = React.useCallback(async () => {
    await refetch();
    pagination.resetPage();
  }, [pagination, refetch]);

  const { setSearchValue, searchValue } = useSearch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo((): Array<TableRowAction<PlatformStaffItemFragment>> => {
    return [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: staff => {
          openModalV2(PlatformStaffDetailModal, {
            id: ModalId.ClearVerifyStaffDetailModal,
            platformStaff: staff,
          });
        },
      },
      {
        startIcon: <AdminPanelSettingsIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'MFA Management',
        hidden: !isAbleToManagePlatformStaff,
        onAction: staff => {
          openModalV2(MfaManagementModal, {
            id: ModalId.MfaManagement,
            userId: staff.user.id,
          });
        },
      },
      {
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'Clear Verify',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToPlatformStaff({
                variables: { platformStaffId: staff.id },
              });
              return data?.resendInviteToPlatformStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      },
      {
        startIcon: <DeleteFilledIcon sx={{ margin: '0 10px 0 0', color: 'error.main' }} />,
        actionName: 'Delete Staff',
        style: { color: theme.palette.error.main },
        hidden: staff => staff.id === platformStaffId || !isAbleToManagePlatformStaff,
        onAction: staff => {
          openModalV2(DeleteClearVerifyStaffConfirmationModal, {
            id: ModalId.DeleteClearVerifyStaffConfirmationModal,
            clearVerifyStaffId: staff.id,
          });
        },
      },
    ];
  }, [
    platformStaffId,
    openModal,
    openModalV2,
    resendInviteToPlatformStaff,
    theme.palette.error.main,
    isAbleToManagePlatformStaff,
  ]);

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.ClearVerifyStaffInviteModal),
        icon: <AddIcon />,
        show: isAbleToManagePlatformStaff,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [openModal, isAbleToManagePlatformStaff],
  );

  return (
    <Table
      dataSource={clearVerifyStaff}
      rowCount={clearVerifyStaffCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      pagination={pagination}
      onSearch={setSearchValue}
      filters={filters}
      onRefresh={refetchData}
      onSort={setSortOption}
      loading={loading || platformStaffLoading}
    />
  );
};

const columns: Array<TableColumn<PlatformStaffItemFragment>> = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, clearVerifyStaffItem, { rowActions }) => {
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(clearVerifyStaffItem)}
          label={getUserFullName(clearVerifyStaffItem.user)}
          labelPostfix={defineStaffLabelPostfix(clearVerifyStaffItem)}
          logoUrl={clearVerifyStaffItem.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, clearVerifyStaffItem) => {
      return <Typography variant="body2">{clearVerifyStaffItem.user.email}</Typography>;
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, clearVerifyStaffItem) => {
      return (
        <Typography variant="body2">
          {parseNullableValue(formatPhoneNumber(clearVerifyStaffItem.user.phoneNumber))}
        </Typography>
      );
    },
  },
  {
    title: 'User Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, clearVerifyStaffItem) => {
      return <UserStatusChip userStatus={clearVerifyStaffItem.user.status} />;
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];

const defineStaffLabelPostfix = (platformStaffItem: PlatformStaffItemFragment) => {
  if (hasOneOfRoles(platformStaffItem, [BusinessRole.PlatformAdmin])) {
    return platformStaffItem.isAbleToManagePlatformStaff ? 'Super Admin' : 'Admin';
  }
  return null;
};
